'use client'

import { Cell, Text } from '@vinted/web-ui'

import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { useLocation } from '@marketplace-web/shared/browser'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { useSession } from '@marketplace-web/shared/session'
import { isCurrentUrl } from '@marketplace-web/shared/utils'

import { clickEvent, donationsClickEvent } from '_libs/common/event-tracker/events'
import {
  ACCOUNT_SETTINGS_URL,
  ACCOUNT_STAFF_SETTINGS_URL,
  BUNDLE_DISCOUNT_SETTINGS_URL,
  DONATIONS_SETTINGS_URL,
  EPR_SETTINGS_URL,
  PAYMENTS_SETTINGS_URL,
  POLICIES_SETTINGS_URL,
  PRIVACY_SETTINGS_URL,
  PROFILE_SETTINGS_URL,
  SECURITY_SETTINGS_URL,
  SHIPPING_SETTINGS_URL,
  SUBSCRIPTION_SETTINGS_URL,
} from 'constants/routes'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import { useFetchEprCountries } from '@marketplace-web/domain/business-accounts-data'

type Props = {
  parentPageUrl?: string
}

const ProfileSideNavigation = ({ parentPageUrl }: Props) => {
  const { relativeUrl } = useLocation()
  const { user: currentUser } = useSession()
  const isBusinessAccount = currentUser?.business

  const isFundraiserEnabled = useAbTest('fundraisers')?.variant === 'on' && !isBusinessAccount
  const isEprFSEnabled = useFeatureSwitch('epr_settings_enabled')
  const isEprAbTestEnabled = useAbTest('epr_test')?.variant === 'on'

  const translate = useTranslate('profile_navigation')

  const { track } = useTracking()
  const { countries } = useFetchEprCountries()

  const sellerCountryInEpr = countries?.some(country => country.code === currentUser?.country_code)
  const showEprLink =
    isEprFSEnabled && isEprAbTestEnabled && isBusinessAccount && sellerCountryInEpr

  const menuItems = [
    {
      url: PROFILE_SETTINGS_URL,
      title: translate('page_title.profile_details'),
    },
    {
      url: ACCOUNT_SETTINGS_URL,
      title: translate('page_title.account_settings'),
    },
    {
      url: ACCOUNT_STAFF_SETTINGS_URL,
      title: translate('page_title.account_staff'),
      disabled: !currentUser?.staff_menu_visible,
    },
    {
      url: SHIPPING_SETTINGS_URL,
      title: translate('page_title.shipping'),
    },
    {
      url: PAYMENTS_SETTINGS_URL,
      title: translate('page_title.payments'),
    },
    {
      url: BUNDLE_DISCOUNT_SETTINGS_URL,
      title: translate('page_title.bundle_discounts'),
      disabled: isBusinessAccount,
    },
    {
      url: DONATIONS_SETTINGS_URL,
      title: translate('page_title.donations'),
      disabled: !isFundraiserEnabled,
      track: () => {
        track(
          donationsClickEvent({
            screen: Screen.ProfileTab,
            target: ClickableElement.EnterDonationFromSettings,
          }),
        )
      },
    },
    {
      url: SUBSCRIPTION_SETTINGS_URL,
      title: translate('page_title.notifications'),
    },
    {
      url: PRIVACY_SETTINGS_URL,
      title: translate('page_title.privacy_settings'),
      track: () => {
        track(
          clickEvent({
            screen: Screen.Settings,
            target: ClickableElement.PrivacySettings,
          }),
        )
      },
    },
    {
      url: SECURITY_SETTINGS_URL,
      title: translate('page_title.security'),
    },
    {
      url: POLICIES_SETTINGS_URL,
      title: translate('page_title.your_polices'),
      disabled: !isBusinessAccount,
    },
    {
      url: EPR_SETTINGS_URL,
      title: translate('page_title.epr_settings'),
      disabled: !showEprLink,
    },
  ].filter(item => !item?.disabled)

  const renderTitle = () => {
    return (
      <Cell theme="transparent">
        <Text as="h1" type={Text.Type.Heading} text={translate('title')} />
      </Cell>
    )
  }

  const renderMenuItems = () => {
    return menuItems.map(({ url, title: itemTitle, track: trackFunction }) => {
      const isChildPage = parentPageUrl ? isCurrentUrl(parentPageUrl, url) : false
      const isActive = isCurrentUrl(relativeUrl, url) || isChildPage

      return (
        <Cell
          key={itemTitle}
          url={url}
          onClick={trackFunction}
          theme="transparent"
          type={Cell.Type.Navigating}
        >
          <Text
            as="span"
            testId={isActive ? 'side-menu-link-active' : 'side-menu-link'}
            theme={isActive ? 'amplified' : 'muted'}
            bold
          >
            {itemTitle}
          </Text>
        </Cell>
      )
    })
  }

  return (
    <nav className="body-content__sidebar">
      {renderTitle()}
      {renderMenuItems()}
    </nav>
  )
}

export default ProfileSideNavigation
